import { TypedDataDomain } from 'abitype';
import { Address, Hex } from 'viem';

import { NULL_ADDRESS } from '../constants/addresses';

interface Signature {
  v: number;
  r: Hex;
  s: Hex;
  signatureType: number;
}

export interface SerializedOrder {
  chainId: number;
  expiry: string;
  feeRecipient: string;
  maker: Address;
  makerAmount: string;
  makerToken: Address;
  pool: string;
  salt: Hex | undefined;
  signature: Signature;
  sender: string;
  taker: string;
  takerAmount: string;
  takerToken: Address;
  takerTokenFeeAmount: string;
  verifyingContract: Address;
}

export interface OrderWithBigInt
  extends Omit<
    SerializedOrder,
    'makerAmount' | 'takerAmount' | 'takerTokenFeeAmount' | 'expiry' | 'salt' | 'signature'
  > {
  makerAmount: bigint;
  takerAmount: bigint;
  takerTokenFeeAmount: bigint;
  expiry: bigint;
  salt: bigint;
}

export const serializeOrder = (order: { [key: string]: any }) => {
  const serializedOrder: { [key: string]: unknown } = {};
  for (const key in order) {
    const isChainId = key === 'chainId';
    const numberButNotChainId = typeof order[key] === 'number' && !isChainId; // chainId should always stay as a number;
    if (order[key]._isBigNumber || typeof order[key] === 'bigint' || numberButNotChainId) {
      serializedOrder[key] = order[key].toString();
    } else {
      serializedOrder[key] = order[key];
    }

    if (typeof serializedOrder[key] === 'object' && key === 'signature') {
      (serializedOrder[key] as { [key: string]: any }).v = parseInt(order[key].v, 10);
    }
  }

  return serializedOrder as unknown as SerializedOrder;
};

// NOTE: Below is copied from @0x/protocol-utils

const EXCHANGE_PROXY_EIP712_DOMAIN_DEFAULT = {
  chainId: 1,
  verifyingContract: NULL_ADDRESS as Address,
  name: 'ZeroEx',
  version: '1.0.0',
};

/**
 * Create an exchange proxy EIP712 domain.
 */
export function createExchangeProxyEIP712Domain(
  chainId?: number | undefined,
  verifyingContract?: Address | undefined,
): TypedDataDomain {
  return {
    ...EXCHANGE_PROXY_EIP712_DOMAIN_DEFAULT,
    ...(chainId ? { chainId } : {}),
    ...(verifyingContract ? { verifyingContract } : {}),
  };
}
